import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { requestHandler, responseErrorHandler, responseHandler } from '@/lib/axios/axiosInterceptor';
import { getQueryParams } from '@/utils/string';

const TIME_OUT = 60000;

export type Params = Record<string, any>;

type ApiFunction<Res = unknown> = (
  url: string,
  params?: Params,
  options?: AxiosRequestConfig
) => Promise<AxiosResponse<Res>>;

type Method = 'get' | 'post' | 'patch' | 'delete' | 'put';

class AxiosClient {
  instance: AxiosInstance;

  constructor(instance: AxiosInstance) {
    this.instance = instance;
  }

  public request = <Res = unknown>(method: Method): ApiFunction<Res> =>
    (
      ({
        get: this.apiGet<Res>(),
        post: this.apiPost<Res>(),
        patch: this.apiPatch<Res>(),
        put: this.apiPut<Res>(),
        delete: this.apiDelete<Res>()
      }) as const
    )[method];

  private apiGet<Res>(): ApiFunction<Res> {
    return (url, queryParams, options) => {
      const requestQueryParams = queryParams
        ? getQueryParams({ params: queryParams, disable: url.includes('/api/public/bulletin') })
        : '';
      const urlWithQueryParams = `${url}${requestQueryParams}`;
      const response = this.instance.get<Res>(urlWithQueryParams, options);
      return response;
    };
  }

  private apiPost<Res>(): ApiFunction<Res> {
    return (url, body, options) => {
      const response = this.instance.post<Res>(url, body, options);
      return response;
    };
  }

  private apiPut<Res>(): ApiFunction<Res> {
    return (url, body, options) => {
      const response = this.instance.put<Res>(url, body, options);
      return response;
    };
  }

  private apiPatch<Res>(): ApiFunction<Res> {
    return (url, body, options) => {
      const response = this.instance.patch<Res>(url, body, options);
      return response;
    };
  }

  private apiDelete<Res>(): ApiFunction<Res> {
    return (url, queryParams, options) => {
      const requestQueryParams = queryParams ? getQueryParams({ params: queryParams }) : '';
      const urlWithQueryParams = `${url}${requestQueryParams}`;
      const response = this.instance.delete<Res>(urlWithQueryParams, options);
      return response;
    };
  }
}

const getApiClient = (instance: AxiosInstance) => {
  const axiosClient = new AxiosClient(instance);

  const api = async <Res = unknown>(
    method: Method,
    url: string,
    params?: Params,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse<Res>> => {
    const result = await axiosClient.request<Res>(method)(url, params, options);

    return result;
  };

  return api;
};

const instance = axios.create({ timeout: TIME_OUT, withCredentials: true });

instance.interceptors.request.use(requestHandler);
instance.interceptors.response.use(responseHandler, responseErrorHandler);

export const api = getApiClient(instance);
